import { apiErrorField } from '@agos/api-client'
import {
  getGetTatrabankaSettingsQueryKey,
  usePatchTatrabankaSettings,
  usePostTatrabankaRunScheduler,
  useGetTatrabankaSettings,
} from '@agos/api-client/payments'
import { T, formatIban, tt, useAuth } from '@agos/shared'
import {
  ConfirmDialog,
  PageHeader,
  ReadFailedBlock,
  Spinner,
  StatusBadge,
  cn,
  useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  Eye,
  EyeOff,
  FileDown,
  Info,
  Play,
  Save,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link } from 'wouter'

import {
  canSave,
  formOf,
  outcomeIsExpected,
  outcomeMessage,
  readOutcome,
  settingProblems,
  settingsPayload,
  type BankForm,
  type BankSettings,
  type SchedulerOutcome,
  type SettingProblem,
} from './bank-settings'
import { bankNote, managesBank } from '../invoicing-rights'

/**
 * `/banking-settings` — the Tatra Banka PSD2 credentials, and the truth about them.
 *
 * **FB-084.** `BLOCKED.md` records that the PSD2 client is an explicit stub: the
 * scheduler finds the due invoices, records a *failed* payment attempt against each one,
 * and leaves them unpaid. It has never paid anybody. Legacy's page offers an enable
 * toggle, credential fields, a *run now* button, and a numbered list that presents PSD2
 * and the SEPA batch as two equal options — with no sign that only one of them works.
 *
 * The credentials are still configured for real: they are what the working client will
 * need the day the developer agreement exists. Only the promise is corrected.
 *
 * Ported from legacy `pages/banking-settings.tsx`.
 */

export function BankingSettingsPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  /* §9.758, BACKEND.md §10A.413 - the bank connection stays the main administrator's: `GET` and `PATCH
     /tatrabanka/settings` and `POST /tatrabanka/run-scheduler` hold the credentials and decide where money goes. The
     route admits everybody holding `banking`, so this page offered them the save and the run, and drew a refused read
     as a failure. Not asked for anybody else, and said instead. */
  const { user } = useAuth()
  const manages = managesBank(user?.systemRole)
  const { data, isLoading, isError, refetch } = useGetTatrabankaSettings({ query: { enabled: manages } })
  const settings = data as BankSettings | undefined

  const [form, setForm] = useState<BankForm>(() => formOf(undefined))
  const [loadedFrom, setLoadedFrom] = useState<BankSettings | undefined>(undefined)
  const [showSecret, setShowSecret] = useState(false)
  const [outcome, setOutcome] = useState<SchedulerOutcome | null>(null)
  const [confirmingRun, setConfirmingRun] = useState(false)

  if (settings && settings !== loadedFrom) {
    setLoadedFrom(settings)
    setForm(formOf(settings))
  }

  const secretStored = settings?.clientSecretSet === true
  const set = <K extends keyof BankForm>(key: K, value: BankForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const problems = settingProblems(form, secretStored)

  const save = usePatchTatrabankaSettings({
    mutation: {
      onSuccess: () => {
        set('clientSecret', '')
        void queryClient.invalidateQueries({ queryKey: getGetTatrabankaSettingsQueryKey() })
        toast({ title: tt('Nastavenia uložené') })
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Nastavenia sa nepodarilo uložiť.'),
          variant: 'destructive',
        }),
    },
  })

  const runScheduler = usePostTatrabankaRunScheduler({
    mutation: {
      onSuccess: (result: unknown) => setOutcome(readOutcome(result)),
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Plánovač sa nepodarilo spustiť.'),
          variant: 'destructive',
        }),
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-5">
        <PageHeader title={tt('Banka - auto-platby')} icon={<Banknote className="w-5 h-5" />} />
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Banka - auto-platby')}
        description={tt('Tatrabanka PSD2 - prístupové údaje a plánovač')}
        icon={<Banknote className="w-5 h-5" />}
      />

      {/* FB-084 - first thing on the page, because everything below it is dormant. */}
      <div
        className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 space-y-2"
        data-psd2-warning
      >
        <p className="text-sm font-bold text-amber-600 dark:text-amber-400 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <T>Automatické platby cez PSD2 zatiaľ nikoho nezaplatia</T>
        </p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          <T>
            Klient pre Tatrabanka PSD2 je zámerne nedokončený - chýbajú produkčné OAuth údaje z
            Tatrabanky a podpísaná SCA zmluva. Plánovač nájde faktúry po termíne, ku každej zapíše
            neúspešný pokus o platbu a nechá ich nezaplatené. Nie je to porucha, je to súčasný stav.
          </T>
        </p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          <T>
            Cesta, ktorá funguje, je SEPA dávka: vo Faktúrach označíte nezaplatené, stiahnete XML
            súbor a nahráte ho do svojho firemného bankovníctva, kde ho potvrdíte tak, ako ste zvyknutí.
          </T>
        </p>
        <Link
          href="/invoices"
          className="inline-flex items-center gap-2 text-xs font-semibold text-primary hover:underline"
        >
          <FileDown className="w-3.5 h-3.5" />
          <T>Prejsť na Faktúry a vygenerovať SEPA dávku</T>
        </Link>
      </div>

      {/* §9.621, FB-929 - **the editor is declined rather than the save disabled.** `formOf(undefined)` is
        * `enabled: false`, both credentials blank, `sandbox: true` and the default advance, and
        * `settingsPayload` sends all five unconditionally. **The credentials survive**, because
        * `TatrabankaSettings.update` is `COALESCE(:clientId, client_id)` and the payload sends a blank as
        * `null`; what a blank form really writes is auto-payments **off**, the advance window reset, and
        * `sandbox: true` - **a live integration moved to the test endpoint**. The button is enabled on that
        * form **by design**: `canSave` refuses only a malformed IBAN, because half-configured is a legitimate
        * state while the bank hands the credentials over one at a time. And blank boxes over credentials that
        * exist mislead even greyed out (§9.531) - the boxes are what somebody reads. */}
      {!manages ? (
        <p
          className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground"
          data-bank-main-admin-only
        >
          {bankNote()}
        </p>
      ) : isError ? (
        <ReadFailedBlock
          marker="read"
          of="banking-settings"
          title={tt('Nastavenia banky sa nepodarilo načítať')}
          onRetry={() => void refetch()}
        >
          <T>
            Preto tu nie je ani formulár - prázdny sa dá uložiť a vypnúť automatické platby aj prepnúť
            integráciu z produkčného režimu do testovacieho.
          </T>
        </ReadFailedBlock>
      ) : (
      <section className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-semibold text-sm">
              <T>Prístupové údaje k PSD2</T>
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              <T>
                Vyplňte ich, keď ich od banky dostanete - pripravia integráciu na deň, keď bude klient
                dokončený.
              </T>
            </p>
          </div>
          <label className="flex items-center gap-2 shrink-0">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => set('enabled', event.target.checked)}
              aria-label={tt('Zapnúť integráciu')}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm font-semibold">
              <T>Zapnuté</T>
            </span>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Client ID">
            <input
              value={form.clientId}
              onChange={(event) => set('clientId', event.target.value)}
              aria-label="Client ID"
              className={INPUT}
              placeholder="tb-client-…"
            />
          </Field>

          <Field
            label="Client Secret"
            hint={
              secretStored
                ? tt('Kľúč je uložený - vyplňte iba pre zmenu')
                : tt('Zatiaľ nie je uložený žiadny kľúč')
            }
          >
            <div className="flex gap-2">
              <input
                type={showSecret ? 'text' : 'password'}
                value={form.clientSecret}
                onChange={(event) => set('clientSecret', event.target.value)}
                aria-label="Client Secret"
                className={cn(INPUT, 'flex-1')}
                placeholder={secretStored ? '•••••••••••' : tt('Zadajte kľúč')}
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                aria-label={showSecret ? tt('Skryť kľúč') : tt('Zobraziť kľúč')}
                className="px-3 rounded-lg border border-border text-muted-foreground hover:text-foreground transition-colors"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>

          <Field
            label={tt('IBAN, z ktorého sa platí')}
            hint={
              form.ibanFrom.trim() !== '' && problems.every((problem) => !problem.kind.startsWith('iban'))
                ? formatIban(form.ibanFrom)
                : undefined
            }
          >
            <input
              value={form.ibanFrom}
              onChange={(event) => set('ibanFrom', event.target.value)}
              aria-label={tt('IBAN, z ktorého sa platí')}
              className={INPUT}
              placeholder="SK00 0000 0000 0000 0000 0000"
            />
          </Field>

          <Field
            label={tt('Minimálny predstih (minúty)')}
            hint={tt('Ako dlho pred termínom smie plánovač platbu vytvoriť')}
          >
            <input
              value={form.minAdvanceMinutes}
              onChange={(event) => set('minAdvanceMinutes', event.target.value)}
              aria-label={tt('Minimálny predstih')}
              className={INPUT}
              placeholder="60"
            />
          </Field>
        </div>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.sandbox}
            onChange={(event) => set('sandbox', event.target.checked)}
            aria-label={tt('Sandbox')}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm">
            <T>Sandbox (testovacie prostredie banky)</T>
          </span>
          {!form.sandbox && (
            <StatusBadge color="red" label={tt('produkcia')} icon={AlertTriangle} />
          )}
        </label>

        {problems.length > 0 && (
          <div className="space-y-1" data-setting-problems>
            {problems.map((problem) => (
              <p
                key={problem.kind}
                className={cn(
                  'flex items-start gap-1.5 text-xs leading-relaxed',
                  canSave([problem]) ? 'text-muted-foreground' : 'text-red-500',
                )}
              >
                <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                <ProblemText problem={problem} />
              </p>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => save.mutate({ data: settingsPayload(form) })}
          disabled={save.isPending || !canSave(problems)}
          className={cn('w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg', BUTTON_PRIMARY)}
        >
          {save.isPending ? <Spinner className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          <T>Uložiť nastavenia</T>
        </button>
      </section>
      )}

      <section className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <h3 className="font-semibold text-sm">
          <T>Plánovač</T>
        </h3>
        <p className="text-xs text-muted-foreground">
          <T>
            Prejde faktúry po termíne a pokúsi sa ich zaplatiť. Kým je klient nedokončený, ku každej
            zapíše neúspešný pokus.
          </T>
        </p>

        {manages && (
          <button
            type="button"
            data-scheduler-run
            onClick={() => setConfirmingRun(true)}
            disabled={runScheduler.isPending}
            className="w-full flex items-center justify-center gap-2 border border-border font-semibold rounded-xl py-2.5 hover:bg-muted/50 transition-colors disabled:opacity-50"
          >
            {runScheduler.isPending ? <Spinner className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            <T>Spustiť plánovač teraz</T>
          </button>
        )}

        {outcome && (
          <div
            data-scheduler-outcome={outcomeIsExpected(outcome) ? 'expected' : 'paid'}
            className={cn(
              'rounded-xl border p-3 text-xs space-y-1',
              outcomeIsExpected(outcome)
                ? 'bg-muted/40 border-border'
                : 'bg-emerald-500/10 border-emerald-500/30',
            )}
          >
            <p className="flex items-start gap-1.5 font-semibold">
              {outcomeIsExpected(outcome) ? (
                <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-px text-emerald-500" />
              )}
              {outcomeMessage(outcome)}
            </p>
            {outcome.skipped === null && (
              <p className="text-muted-foreground tabular-nums">
                <T>Prejdených</T> {outcome.scanned} · <T>zaplatených</T> {outcome.paid} ·{' '}
                <T>neúspešných</T> {outcome.failed}
              </p>
            )}
            {outcome.skipped !== null && (
              <p className="text-muted-foreground">
                <T>Dôvod:</T> {outcome.skipped}
              </p>
            )}
          </div>
        )}
      </section>

      <ConfirmDialog
        open={confirmingRun}
        onClose={() => setConfirmingRun(false)}
        onConfirm={() => {
          setConfirmingRun(false)
          setOutcome(null)
          runScheduler.mutate()
        }}
        title={tt('Spustiť plánovač platieb?')}
        // Not destructive today, and it writes a failed attempt against every due
        // invoice — which is a record somebody will read later.
        description={tt(
          'Ku každej faktúre po termíne zapíše pokus o platbu. Kým je PSD2 klient nedokončený, každý pokus bude neúspešný a faktúry zostanú nezaplatené.',
        )}
        confirmLabel={tt('Spustiť')}
      />
    </div>
  )
}

function ProblemText({ problem }: { problem: SettingProblem }) {
  if (problem.kind === 'no-client-id') return <T>Na zapnutie treba Client ID.</T>
  if (problem.kind === 'no-secret') return <T>Na zapnutie treba Client Secret.</T>
  if (problem.kind === 'no-iban') return <T>Na zapnutie treba IBAN, z ktorého sa platí.</T>
  if (problem.kind === 'iban-checksum') {
    return (
      <T>
        IBAN nemá platnú kontrolnú číslicu. Z takého účtu sa platba neodošle, alebo pôjde niekam inam.
      </T>
    )
  }
  if (problem.kind === 'iban-length') {
    return (
      <span>
        <T>Slovenský IBAN má 24 znakov, tento má</T> {problem.value.replace(/\s/g, '').length}.
      </span>
    )
  }
  if (problem.kind === 'advance-not-a-number') {
    return <T>Predstih musí byť číslo v minútach.</T>
  }
  return <T>Predstih nemôže byť negatívny.</T>
}

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground/80 font-mono">{hint}</p>}
    </div>
  )
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
